
import React, { createContext, useContext } from 'react';
import { Task } from '@/types/tasks';
import { DateRangeOption } from '@/hooks/tasks/use-task-operations';
import { useTaskProvider } from './useTaskProvider';
import { useTaskContextTracking } from '@/hooks/tasks/useTaskContextTracking';

// Define the TaskContext type
export interface TaskContextType {
  activeTasks: Task[];
  completedTasks: Task[];
  activeTaskCount: number;
  isLoading: boolean;
  setActiveTasks: (tasks: Task[]) => void;
  setCompletedTasks: (tasks: Task[]) => void;
  addTask: (task: Omit<Task, 'id' | 'isCompleted' | 'createdAt'>) => void;
  completeTask: (taskId: string) => void;
  uncompleteTask: (taskId: string) => void;
  removeTask: (taskId: string) => void;
  clearCompletedTasks: () => void;
  updateTask: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  updateTaskOrder: (taskId: string, newOrder: number) => void;
  setDateRange: (range: DateRangeOption) => void;
  setCurrentDate: (date: Date) => void;
  dateRange: DateRangeOption;
  currentDate: Date;
  refetchTasks: () => void;
  // All/Now toggle for active tasks
  showOnlyCurrentRange: boolean;
  setShowOnlyCurrentRange: (value: boolean) => void;
  // Context tracking functionality
  trackTaskChange: (taskId: string, changeType: 'create' | 'edit' | 'complete') => void;
  isProcessingContext: boolean;
  pendingUpdatesCount: number;
  canPerformOperation: (operation: 'generate' | 'edit' | 'complete' | 'create') => boolean;
  waitForContextProcessing: () => Promise<void>;
}

// Create the context
const TaskContext = createContext<TaskContextType | undefined>(undefined);

// Provider component
export const TaskProvider = ({ children }: { children: React.ReactNode }) => {
  // Use our custom hook to handle all the state and logic
  const taskProviderValue = useTaskProvider();
  
  // Add context tracking functionality
  const contextTracking = useTaskContextTracking();
  
  // Merge both contexts
  const contextValue: TaskContextType = {
    ...taskProviderValue,
    ...contextTracking
  };

  return (
    <TaskContext.Provider value={contextValue}>
      {children}
    </TaskContext.Provider>
  );
};

// Hook to use the task context
export const useTasks = (): TaskContextType => {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
};
